import React from 'react';
import { Label, Button, DownloadFileLink } from 'fw/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import CapabilitiesPopUp from '../popups/CapabilitiesPopUp';
import AccreditationsPopUp from '../popups/AccreditationsPopUp';
import EmployeesPopUp from '../popups/EmployeesPopUp';
import NaicsCodesPopUp from '../popups/NaicsCodesPopUp';

// NVBDC-1404 — Company Capabilities / Accreditations / NAICS Codes / Employees, each with its
// own pencil-icon popup (plan items 12-13), local-state-only.
class BusinessStructureSection extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            showCapabilities: false,
            showAccreditations: false,
            showEmployees: false,
            showNaicsCodes: false
        };
    }

    open = (key) => this.setState({ [key]: true });
    close = (key) => this.setState({ [key]: false });

    renderTextValue = (value, id) => (
        value
            ? <p id={id} className="one-sheet-field-value">{value}</p>
            : <p id={id} className="one-sheet-field-value one-sheet-field-empty">Not yet provided</p>
    );

    // DownloadFileLink already guards on storageToken == null, so this renders nothing when no
    // file was uploaded — no extra empty-state needed here beyond renderTextValue's above.
    renderFileName = (storageToken, fileName) => (
        <div className="one-sheet-field-file">
            <DownloadFileLink
                storageToken={storageToken}
                fileName={fileName}
                buttonName={fileName}
                buttonStyle="link"
                wrapFileNameLength={40}
            />
        </div>
    );

    // Not `??` — this project's Babel config doesn't parse nullish coalescing (confirmed live:
    // "Failed to compile... Unexpected token" on `bs.amountFullTimeEmployee ?? '—'`). Explicit
    // null/undefined check instead of `||`, since `||` would also replace a real `0` count.
    formatCount = (value) => (value === null || value === undefined ? '—' : value);

    onCapabilitiesSave = (fields) => {
        this.props.onChange({ ...this.props.businessStructure, ...fields });
    };

    onAccreditationsSave = (fields) => {
        this.props.onChange({ ...this.props.businessStructure, ...fields });
    };

    onEmployeesSave = (fields) => {
        this.props.onChange({ ...this.props.businessStructure, ...fields });
    };

    onNaicsCodesSave = (naicsCodes) => {
        this.props.onChange({ ...this.props.businessStructure, naicsCodes });
    };

    render() {
        const bs = this.props.businessStructure;
        if (!bs) return null;

        return (
            <div>
                <div className="one-sheet-field-block">
                    <div className="one-sheet-field-header">
                        <Label>Company Capabilities</Label>
                        <Button id="btnEditCapabilities" bsStyle="link" onClick={() => this.open('showCapabilities')}>
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                    </div>
                    {this.renderTextValue(bs.capabilitiesStatement, 'oneSheetCapabilitiesText')}
                    {this.renderFileName(bs.capabilitiesStatementStorageToken, bs.capabilitiesStatementFileName)}
                </div>

                <div className="one-sheet-field-block">
                    <div className="one-sheet-field-header">
                        <Label>Company Accreditations</Label>
                        <Button id="btnEditAccreditations" bsStyle="link" onClick={() => this.open('showAccreditations')}>
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                    </div>
                    {this.renderTextValue(bs.professionalAccreditation)}
                    {this.renderFileName(bs.professionalAccreditationStorageToken, bs.professionalAccreditationFileName)}
                </div>

                <div className="one-sheet-field-block">
                    <div className="one-sheet-field-header">
                        <Label>Company NAICS Codes</Label>
                        <Button id="btnEditNaicsCodes" bsStyle="link" onClick={() => this.open('showNaicsCodes')}>
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                    </div>
                    {(bs.naicsCodes || []).length > 0 ? (
                        <ul id="oneSheetNaicsCodesList" className="one-sheet-naics-list">
                            {bs.naicsCodes.map((n, i) => <li key={i}>{n.naicsDescription}</li>)}
                        </ul>
                    ) : this.renderTextValue(null, 'oneSheetNaicsCodesList')}
                </div>

                <div className="one-sheet-field-block">
                    <div className="one-sheet-field-header">
                        <Label>Company Employees and 1099s</Label>
                        <Button id="btnEditEmployees" bsStyle="link" onClick={() => this.open('showEmployees')}>
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                    </div>
                    <div id="oneSheetEmployeeCounts">
                        <div className="one-sheet-financials-row"><span>Number of Full-time employees</span><span>{this.formatCount(bs.amountFullTimeEmployee)}</span></div>
                        <div className="one-sheet-financials-row"><span>Number of Veteran employees</span><span>{this.formatCount(bs.amountVeteranEmployee)}</span></div>
                        <div className="one-sheet-financials-row"><span>Number of Part-time employees</span><span>{this.formatCount(bs.amountPartTimeEmployee)}</span></div>
                        <div className="one-sheet-financials-row"><span>Number of 1099 Recipients</span><span>{this.formatCount(bs.amountContractor)}</span></div>
                    </div>
                </div>

                <CapabilitiesPopUp
                    visible={this.state.showCapabilities}
                    capabilitiesStatement={bs.capabilitiesStatement}
                    capabilitiesStatementStorageToken={bs.capabilitiesStatementStorageToken}
                    capabilitiesStatementFileName={bs.capabilitiesStatementFileName}
                    onSave={this.onCapabilitiesSave}
                    onClose={() => this.close('showCapabilities')}
                />
                <AccreditationsPopUp
                    visible={this.state.showAccreditations}
                    professionalAccreditation={bs.professionalAccreditation}
                    professionalAccreditationStorageToken={bs.professionalAccreditationStorageToken}
                    professionalAccreditationFileName={bs.professionalAccreditationFileName}
                    onSave={this.onAccreditationsSave}
                    onClose={() => this.close('showAccreditations')}
                />
                <EmployeesPopUp
                    visible={this.state.showEmployees}
                    amountFullTimeEmployee={bs.amountFullTimeEmployee}
                    amountVeteranEmployee={bs.amountVeteranEmployee}
                    amountPartTimeEmployee={bs.amountPartTimeEmployee}
                    amountContractor={bs.amountContractor}
                    onSave={this.onEmployeesSave}
                    onClose={() => this.close('showEmployees')}
                />
                <NaicsCodesPopUp
                    visible={this.state.showNaicsCodes}
                    naicsCodes={bs.naicsCodes}
                    onSave={this.onNaicsCodesSave}
                    onClose={() => this.close('showNaicsCodes')}
                />
            </div>
        );
    }
}

export default BusinessStructureSection;



// WEBPACK FOOTER //
// ./src/areas/certification/oneSheet/sections/BusinessStructureSection.js